import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Eye, Lock, Database, Bell, Users } from 'lucide-react';

const PrivacyPage = () => {
    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Link 
                                to="/login" 
                                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            >
                                <ArrowLeft className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                            </Link>
                            <div className="flex items-center gap-2">
                                <Shield className="w-6 h-6 text-[#8A78F0]" />
                                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Kebijakan Privasi</h1>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <Lock className="w-5 h-5 text-green-500" />
                            <span className="text-sm text-gray-600 dark:text-gray-400">Terakhir diperbarui: {new Date().toLocaleDateString('id-ID')}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-8">
                    
                    {/* Introduction */}
                    <div className="mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                            Komitmen Kami Terhadap Privasi Anda
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                            LakuKelas menghargai dan melindungi privasi pengguna kami. Kebijakan privasi ini menjelaskan bagaimana kami mengumpulkan, menggunakan, dan melindungi informasi pribadi Anda saat menggunakan layanan kami.
                        </p>
                    </div>

                    {/* Sections */}
                    <div className="space-y-8">
                        {/* Informasi yang Kami Kumpulkan */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <Database className="w-5 h-5 text-[#8A78F0]" />
                                1. Informasi yang Kami Kumpulkan
                            </h3>
                            <div className="space-y-4">
                                <div>
                                    <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Informasi Pribadi Guru:</h4>
                                    <ul className="space-y-2 text-gray-600 dark:text-gray-400 ml-4">
                                        <li>• Nama lengkap dan gelar akademik</li>
                                        <li>• Alamat email dan nomor telepon</li>
                                        <li>• Informasi institusi pendidikan</li>
                                        <li>• Foto profil (opsional)</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Data Akademik Siswa:</h4>
                                    <ul className="space-y-2 text-gray-600 dark:text-gray-400 ml-4">
                                        <li>• Nama siswa dan nomor induk</li>
                                        <li>• Data absensi dan nilai</li>
                                        <li>• Jurnal pembelajaran</li>
                                        <li>• Informasi kelas dan mata pelajaran</li>
                                    </ul>
                                </div>
                                <div>
                                    <h4 className="font-medium text-gray-800 dark:text-gray-200 mb-2">Data Teknis:</h4>
                                    <ul className="space-y-2 text-gray-600 dark:text-gray-400 ml-4">
                                        <li>• Alamat IP dan informasi perangkat</li>
                                        <li>• Log aktivitas dan penggunaan</li>
                                        <li>• Cookie dan teknologi pelacakan</li>
                                    </ul>
                                </div>
                            </div>
                        </section>

                        {/* Cara Kami Menggunakan Informasi */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <Eye className="w-5 h-5 text-[#8A78F0]" />
                                2. Cara Kami Menggunakan Informasi
                            </h3>
                            <div className="space-y-3 text-gray-600 dark:text-gray-400">
                                <p>• Menyediakan dan memelihara layanan LakuKelas</p>
                                <p>• Memproses dan menyimpan data akademik siswa</p>
                                <p>• Mengirim notifikasi dan pembaruan penting</p>
                                <p>• Meningkatkan kualitas layanan dan pengalaman pengguna</p>
                                <p>• Mematuhi kewajiban hukum dan regulasi</p>
                                <p>• Mencegah penipuan dan penyalahgunaan sistem</p>
                            </div>
                        </section>

                        {/* Berbagi Informasi */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <Users className="w-5 h-5 text-[#8A78F0]" />
                                3. Berbagi Informasi
                            </h3>
                            <div className="space-y-3 text-gray-600 dark:text-gray-400">
                                <p>• Kami tidak akan menjual, menyewakan, atau membagikan data pribadi Anda kepada pihak ketiga tanpa izin yang jelas.</p>
                                <p>• Data dapat dibagikan dengan penyedia layanan yang membantu kami mengoperasikan platform (dengan perjanjian kerahasiaan).</p>
                                <p>• Informasi dapat dibagikan jika diperlukan oleh hukum atau untuk melindungi hak dan keamanan pengguna.</p>
                                <p>• Data agregat (tanpa identitas pribadi) dapat digunakan untuk analisis dan penelitian.</p>
                            </div>
                        </section>

                        {/* Keamanan Data */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <Lock className="w-5 h-5 text-[#8A78F0]" />
                                4. Keamanan Data
                            </h3>
                            <div className="space-y-3 text-gray-600 dark:text-gray-400">
                                <p>• Kami menggunakan enkripsi SSL/TLS untuk melindungi data dalam transit</p>
                                <p>• Data disimpan di server yang aman dengan akses terbatas</p>
                                <p>• Kami secara berkala memantau dan memperbarui sistem keamanan</p>
                                <p>• Staf kami dilatih tentang praktik keamanan data yang baik</p>
                                <p>• Kami memiliki protokol untuk menangani pelanggaran keamanan</p>
                            </div>
                        </section>

                        {/* Hak Pengguna */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                5. Hak Pengguna
                            </h3>
                            <div className="space-y-3 text-gray-600 dark:text-gray-400">
                                <p>• Hak untuk mengakses dan melihat data pribadi Anda</p>
                                <p>• Hak untuk memperbaiki atau memperbarui informasi yang tidak akurat</p>
                                <p>• Hak untuk meminta penghapusan data pribadi (hak untuk dilupakan)</p>
                                <p>• Hak untuk membatasi pemrosesan data</p>
                                <p>• Hak untuk memindahkan data ke platform lain</p>
                                <p>• Hak untuk menarik persetujuan pemrosesan data</p>
                            </div>
                        </section>

                        {/* Cookie dan Teknologi Pelacakan */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                6. Cookie dan Teknologi Pelacakan
                            </h3>
                            <div className="space-y-3 text-gray-600 dark:text-gray-400">
                                <p>• Kami menggunakan cookie untuk meningkatkan pengalaman pengguna</p>
                                <p>• Cookie membantu mengingat preferensi dan sesi login</p>
                                <p>• Anda dapat mengatur browser untuk menolak cookie</p>
                                <p>• Beberapa fitur mungkin tidak berfungsi optimal tanpa cookie</p>
                            </div>
                        </section>

                        {/* Retensi Data */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                7. Retensi Data
                            </h3>
                            <div className="space-y-3 text-gray-600 dark:text-gray-400">
                                <p>• Data pribadi disimpan selama akun aktif dan diperlukan untuk layanan</p>
                                <p>• Data akademik siswa disimpan sesuai dengan kebijakan institusi pendidikan</p>
                                <p>• Data dapat dihapus setelah periode retensi yang ditentukan</p>
                                <p>• Backup data dapat disimpan untuk keperluan pemulihan sistem</p>
                            </div>
                        </section>

                        {/* Anak di Bawah Umur */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                8. Perlindungan Anak di Bawah Umur
                            </h3>
                            <div className="space-y-3 text-gray-600 dark:text-gray-400">
                                <p>• LakuKelas tidak ditujukan untuk anak di bawah 13 tahun</p>
                                <p>• Data siswa di bawah 18 tahun dikelola oleh guru dan institusi pendidikan</p>
                                <p>• Kami mematuhi regulasi perlindungan data anak</p>
                                <p>• Orang tua dapat menghubungi kami untuk pertanyaan tentang data anak</p>
                            </div>
                        </section>

                        {/* Perubahan Kebijakan */}
                        <section>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                9. Perubahan Kebijakan Privasi
                            </h3>
                            <div className="space-y-3 text-gray-600 dark:text-gray-400">
                                <p>• Kebijakan ini dapat diperbarui dari waktu ke waktu</p>
                                <p>• Perubahan signifikan akan diumumkan melalui aplikasi atau email</p>
                                <p>• Tanggal pembaruan terakhir akan ditampilkan di bagian atas halaman</p>
                                <p>• Penggunaan berkelanjutan berarti Anda menyetujui kebijakan yang diperbarui</p>
                            </div>
                        </section>
                    </div>

                    {/* Contact Information */}
                    <div className="mt-12 p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                            Hubungi Kami
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-2">
                            Jika Anda memiliki pertanyaan tentang kebijakan privasi ini, silakan hubungi kami:
                        </p>
                        <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                            <p>📧 Email: balkhi005@gmail.com</p>
                            <p>📱 WhatsApp: +62 89522537711</p>
                            <p>🌐 Website: www.lakukels.my.id</p>
                            <p>📮 Alamat: Jl. arjuna No. 48 Desa Pagiyanten, Kab. tegal, jawa tengah, Indonesia</p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            © {new Date().getFullYear()} LakuKelas. Semua hak dilindungi.
                        </p>
                        <div className="mt-4 flex justify-center gap-4">
                            <Link 
                                to="/terms" 
                                className="text-sm text-[#8A78F0] hover:underline"
                            >
                                Syarat & Ketentuan
                            </Link>
                            <Link 
                                to="/login" 
                                className="text-sm text-[#8A78F0] hover:underline"
                            >
                                Kembali ke Login
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPage; 